import omit from "lodash/omit";
import { useActivityCurrentUserRole, useActivityPermissions } from "PFApp/activities/hooks";
import { TooltipButtonIcon } from "PFApp/activities/show/activity_page/parts/tooltip_button_icon";
import { useActivityShowContext } from "PFApp/activities/show/context/activity_show_context";
import { useActivityShortlists } from "PFApp/activities/show/hooks/use_activity_shortlists";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import RefreshAction from "PFApp/activities/show/parts/refresh_action";
import { useShortlistsActions } from "PFApp/activities/show/store/shortlists";
import { ProfilesListMode } from "PFApp/activities/types/profiles_view_mode";
import { CvExportsButton } from "PFApp/components/cv_exports_button";
import {
  FiltersV2TermSearch,
  getVisibleFilters,
  useFiltersV2Actions,
  useFiltersV2Store
} from "PFApp/components/filters_v2";
import { SearchEntity } from "PFApp/components/search_autocomplete/search_autocomplete.types";
import { TablePropertiesButton } from "PFApp/components/table_properties/table_properties_button";
import { TablePropertiesSidePanel } from "PFApp/components/table_properties/table_properties_side_panel";
import { Alert } from "PFComponents/alert";
import { Modal } from "PFComponents/modal";
import { areFiltersModified } from "PFCore/helpers/filters";
import { useShortlistsInvalidate } from "PFCore/hooks/queries/shortlists/use_shortlists_invalidate";
import { useIsUnbiasedMode } from "PFCore/hooks/use_is_unbiased_mode";
import { KnownFiltersV2 } from "PFTypes/filters_v2";
import { RecentSearchesPageKey } from "PFTypes/recent_searches";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { useToggle } from "react-use";

import { OrderSelector } from "../../../../components/order_selector";
import { ProfileViewModeToggle } from "../../../../components/profiles_view_mode_toggle";
import { AddShortlistModal } from "./modals/add_shortlist_modal";
import css from "./shortlist_header.module.scss";

type ShortlistHeaderProps = {
  handleClearShortlist: VoidFunction;
};

export const ShortlistHeader = ({ handleClearShortlist }: ShortlistHeaderProps) => {
  const { t } = useTranslation(["activities", "filters"]);

  const navigate = useNavigate();
  const location = useLocation();

  const {
    availableProfilesListColumns,
    selectedProfilesListColumns,
    profilesListMode,
    setSelectedProfilesListColumns
  } = useActivityShowContext();

  const activity = useCurrentActivity();
  const { shortlists, allShortlists, shortlistsMeta, suspendedShortlistsMeta } = useActivityShortlists();
  const { updateShortlistsParams } = useShortlistsActions();

  const { isReviewer } = useActivityCurrentUserRole(activity);
  const isUnbiasedMode = useIsUnbiasedMode(activity?.reviewers);
  const { invalidateShortlists } = useShortlistsInvalidate();

  const filtersAreModified = useFiltersV2Store((state) =>
    areFiltersModified(omit(getVisibleFilters(state.mainFilters), [KnownFiltersV2.Availability]))
  );
  const { resetValues } = useFiltersV2Actions();

  const [isClearShortlistConfirmModalOpen, toggleClearShortlistConfirmModalOpen] = useToggle(false);
  const [isAddShortlistModalOpen, toggleAddShortlistModalOpen] = useToggle(false);
  const [isTablePropertiesVisible, setTablePropertiesVisibility] = useState(false);

  useEffect(() => {
    if (profilesListMode === ProfilesListMode.Cards) {
      setTablePropertiesVisibility(false);
    }
  }, [profilesListMode]);

  const { canAddShortlist } = useActivityPermissions(activity);

  const handleCompareAll = () => {
    navigate(`/activities/${activity!.id}/compare/details`, {
      state: { prevPath: `${location.pathname}${location.search}` }
    });
  };

  const isCompleted = activity.state === "complete";
  const isCompareAllDisabled = shortlists.length < 2;
  const isClearShortlistDisabled = allShortlists.filter(({ state }) => state === "pending").length === 0;

  const resultsLabel = useMemo(() => {
    const total = shortlistsMeta.total + suspendedShortlistsMeta.total;

    if (total === 0) {
      return null;
    }

    return t(
      `activities:show.shortlist.${
        filtersAreModified ? "shortlistsPaginationInfoFiltered" : "shortlistsPaginationInfo"
      }`,
      { count: total }
    );
  }, [filtersAreModified, shortlistsMeta, suspendedShortlistsMeta, t]);

  return (
    <>
      <div className={css.container}>
        <FiltersV2TermSearch
          pageKey={RecentSearchesPageKey.ActivityShortlist}
          autocompleteConfig={[
            {
              entity: SearchEntity.Profile,
              useAsTerm: true
            }
          ]}
          includeRecentSearches={false}
          qaId="ActivityShortlistTabSearchInput"
          narrow
          disabled={isUnbiasedMode}
        />
        <OrderSelector
          meta={shortlistsMeta}
          onOrderChange={(order) => updateShortlistsParams({ order })}
          label={resultsLabel}
          className={css.sortByBlock}
        />
        <ProfileViewModeToggle />
        {profilesListMode === ProfilesListMode.Table && (
          <TablePropertiesButton onClick={() => setTablePropertiesVisibility(true)} />
        )}
        <TooltipButtonIcon
          icon="compare"
          onClick={handleCompareAll}
          tooltipContent={
            isCompareAllDisabled
              ? t("activities:show.iconsTooltips.compareAllDisabled")
              : t("activities:show.iconsTooltips.compareAll")
          }
          disabled={isCompareAllDisabled}
          qaId="ActivityShortlistTabCompareButton"
        />
        {!(isReviewer && isUnbiasedMode) && canAddShortlist && (
          <>
            <TooltipButtonIcon
              icon="add-profile"
              onClick={() => toggleAddShortlistModalOpen()}
              tooltipContent={t("activities:show.iconsTooltips.addToShortlist")}
              qaId="ActivityShortlistTabAddProfileButton"
            />
            <TooltipButtonIcon
              icon="remove-all"
              onClick={() => toggleClearShortlistConfirmModalOpen()}
              tooltipContent={
                isClearShortlistDisabled
                  ? t("activities:show.iconsTooltips.clearShortlistDisabled")
                  : t("activities:show.iconsTooltips.clearShortlist")
              }
              qaId="ActivityShortlistTabRemoveAllButton"
              disabled={isClearShortlistDisabled}
            />
          </>
        )}
        <CvExportsButton
          profileIds={(shortlists || []).map(({ profile }) => profile.id)}
          activityId={activity.id}
          tooltipContent={t("activities:show.iconsTooltips.export")}
          qaId="ActivityShortlistTabExportButton"
        />
        {!isCompleted && activity.coowned && (
          <RefreshAction
            activityId={activity.id}
            onUpdate={() => {
              invalidateShortlists();
            }}
            showLabel={false}
            qaId="ActivityShortlistTabRefreshMatchesButton"
          />
        )}
      </div>
      {filtersAreModified && (
        <Alert
          title={t("activities:show.shortlist.filtersChangedWarning")}
          action={{
            text: t("filters:panel.clearAll"),
            icon: "filter-clean",
            kind: "text",
            onClick: () => resetValues()
          }}
        />
      )}
      {isClearShortlistConfirmModalOpen && (
        <Modal
          title={t("activities:show.shortlist.clearShortlist")}
          onOK={handleClearShortlist}
          onClose={() => toggleClearShortlistConfirmModalOpen()}
          labelOK={t("activities:show.shortlist.yesRemove")}
          kindOK="danger"
        >
          {t("activities:show.shortlist.wantClearPendingShortlists")}
        </Modal>
      )}
      {isAddShortlistModalOpen && <AddShortlistModal onClose={() => toggleAddShortlistModalOpen()} />}
      <TablePropertiesSidePanel
        isVisible={isTablePropertiesVisible}
        onCloseClick={() => setTablePropertiesVisibility(false)}
        availableColumns={availableProfilesListColumns}
        selectedColumns={selectedProfilesListColumns}
        onColumnsChange={setSelectedProfilesListColumns}
      />
    </>
  );
};
